import { addBreadcrumb } from '@sentry/react';
import { UserWithBrokerMandate } from 'models/user';
import endpoint from 'shared/api';

import {
  EMAIL_UPDATE_SEND_MAGIC_LINK,
  EMAIL_UPDATE_START,
  EMAIL_UPDATE_UPDATE_EMAIL,
  EMAIL_UPDATE_VERIFY_CURRENT_EMAIL,
  EMAIL_UPDATE_VERIFY_MAGIC_LINK,
} from '../constants';

export type EmailUpdateResponse = {
  requestId: 'string';
};

export const getAccountInfo = async (): Promise<UserWithBrokerMandate> => {
  const { data } = await endpoint.getAccountInfo();
  return data;
};

export const start = async (): Promise<EmailUpdateResponse> => {
  addBreadcrumb({
    category: 'EMAIL_UPDATE',
    message: 'Starting email update process',
    level: 'info',
  });

  const { data } = await endpoint.network.post(EMAIL_UPDATE_START);
  return data;
};

type VerifyCurrentEmailInput = {
  code: string;
  requestId: string;
};

export const verifyCurrentEmail = async ({
  code,
  requestId,
}: VerifyCurrentEmailInput): Promise<EmailUpdateResponse> => {
  addBreadcrumb({
    category: 'EMAIL_UPDATE',
    message: 'Verifying current email with code',
    level: 'info',
    data: { requestId },
  });

  const { data } = await endpoint.network.post(
    EMAIL_UPDATE_VERIFY_CURRENT_EMAIL,
    {
      requestId,
      code,
    }
  );
  return data;
};

type SendMagicLinkInput = {
  requestId: string;
  newEmail: string;
};

export const sendMagicLink = async ({
  requestId,
  newEmail,
}: SendMagicLinkInput): Promise<void> => {
  addBreadcrumb({
    category: 'EMAIL_UPDATE',
    message: 'Sending magic link to new email',
    level: 'info',
    data: { requestId, newEmail },
  });

  const { data } = await endpoint.network.post(EMAIL_UPDATE_SEND_MAGIC_LINK, {
    requestId,
    newEmail,
  });
  return data;
};

export type VerifyMagicLinkResponse = {
  requestId: string;
  newEmail: string;
};

export const verifyMagicLink = async (
  magicLinkToken: string
): Promise<VerifyMagicLinkResponse> => {
  addBreadcrumb({
    category: 'EMAIL_UPDATE',
    message: 'Verifying magic link token',
    level: 'info',
    data: { magicLinkToken: `${magicLinkToken.substring(0, 10)}...` },
  });

  const { data } = await endpoint.network.post(EMAIL_UPDATE_VERIFY_MAGIC_LINK, {
    magicLinkToken,
  });
  return data;
};

export const updateEmail = async (requestId: string): Promise<void> => {
  addBreadcrumb({
    category: 'EMAIL_UPDATE',
    message: 'Finalizing email update',
    level: 'info',
    data: { requestId },
  });

  const { data } = await endpoint.network.patch(EMAIL_UPDATE_UPDATE_EMAIL, {
    requestId,
  });
  return data;
};
